import { useEffect } from 'react'
import { useApolloClient } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { useAuth } from '../../components/provider/OIDCProvider'
import useNotification from './useNotification'
import { getActiveRoleStorageKey, getStoredActiveRole, parseActiveRole } from '../activeRole'

/**
 * Keeps the active persona consistent across browser tabs for dual-role
 * (student/advisor) users. When another tab persists a role switch, the
 * `storage` event fires here and this tab performs the same teardown as an
 * active switch: clear the Apollo cache, then activate the new role and
 * navigate home together, and tell the user why the view changed. No-ops when
 * the value matches the current role, which also breaks the cross-tab echo
 * loop.
 *
 * @param enabled Whether the dual-role switcher feature is on for the
 *   institution. When false the listener is not installed, so a stray storage
 *   write can never persona-scope a user App keeps in merged (legacy) mode.
 */
const useRoleSyncAcrossTabs = (enabled: boolean) => {
  const { user, activeRole, setActiveRole } = useAuth()
  const client = useApolloClient()
  const history = useHistory()
  const { showInfoNotif } = useNotification()
  const sub = user?.sub

  useEffect(() => {
    if (!enabled || !sub || !setActiveRole) {
      return undefined
    }
    const storageKey = getActiveRoleStorageKey(sub)

    const onStorage = async (event: StorageEvent) => {
      if (event.key !== storageKey) {
        return
      }
      const next = parseActiveRole(event.newValue)
      if (!next || next === activeRole) {
        return
      }
      // Clear persona-scoped cache first so no prior-persona data leaks.
      try {
        await client.clearStore()
      } catch {
        // Best-effort cache clear; still activate the synced persona below.
      }
      // Re-read the authoritative stored role after the async gap: rapid
      // cross-tab switches can supersede `next` while clearStore() is pending,
      // so converging on the freshly-read value keeps this tab in step with
      // what storage actually says. If it settled back to what we already
      // show, there is nothing to do.
      const latest = getStoredActiveRole(sub) ?? next
      if (latest === activeRole) {
        return
      }
      // Activate the new persona, THEN navigate home, so the persona-dependent
      // home route ("/") renders under the new persona. Navigating first (under
      // the old persona) let its landing redirect bounce the URL back to the
      // previous page, stranding the tab on stale content after the sync
      // (CDE-1221, mirroring the same fix in useRoleSwitch).
      setActiveRole(latest)
      history.push('/')
      showInfoNotif(
        `Your view changed to ${latest === 'advisor' ? 'advising' : 'student'} in another tab`
      )
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [enabled, sub, activeRole, setActiveRole, client, history, showInfoNotif])
}

export default useRoleSyncAcrossTabs
